// Guides « Conseils cheveux afro » — contenus prudents, sans promesse médicale.
const NET = "https://ozentya.net";

export const HAIR_HUB = {
  path: "/conseils-cheveux-afro",
  title: "Conseils cheveux afro : pousse, casse et chute | Ozentya",
  description:
    "Guides pratiques et prudents pour prendre soin des cheveux afro et crépus : favoriser la longueur, limiter la casse et savoir quand consulter en cas de chute.",
  h1: "Conseils cheveux afro et crépus",
  intro:
    "Des repères simples pour mieux comprendre vos cheveux afro : comment ils poussent, pourquoi ils cassent et quand une chute mérite l'avis d'un professionnel de santé. Ces guides sont informatifs et ne remplacent pas une consultation médicale.",
};

export const HAIR_GUIDES = [
  {
    slug: "pousse-cheveux-afro",
    breadcrumb: "Pousse des cheveux afro",
    title: "Pousse des cheveux afro : comprendre et garder la longueur | Ozentya",
    description:
      "Pourquoi les cheveux afro semblent ne pas pousser ? Le vrai enjeu est souvent la rétention de longueur. Routine simple, gestes utiles et attentes réalistes.",
    h1: "Pousse des cheveux afro : comprendre pour garder la longueur",
    intro:
      "Les cheveux afro poussent, en moyenne, à un rythme comparable aux autres types de cheveux (de l'ordre d'un centimètre par mois, avec de fortes variations d'une personne à l'autre). Leur forme très bouclée les rend plus fragiles et masque la longueur réelle : la difficulté principale est souvent de conserver la longueur, plutôt que de « faire pousser » le cheveu.",
    sections: [
      {
        h: "Pourquoi on a l'impression que ses cheveux ne poussent pas",
        list: [
          "La rétractation : un cheveu crépu peut paraître bien plus court qu'il ne l'est réellement une fois étiré.",
          "La casse des pointes : si les longueurs cassent aussi vite qu'elles poussent, la longueur visible stagne.",
          "La sécheresse : un cheveu déshydraté devient cassant et s'abîme au démêlage.",
          "Les manipulations répétées : coiffages serrés, brossages fréquents et chaleur fragilisent la fibre.",
        ],
      },
      {
        h: "Les gestes qui aident à retenir la longueur",
        list: [
          "Hydrater régulièrement, puis sceller l'hydratation avec une crème ou une huile (méthodes LOC / LCO).",
          "Démêler sur cheveux humides et enrichis en soin, doucement, des pointes vers les racines.",
          "Limiter la chaleur et privilégier des coiffures protectrices pas trop serrées.",
          "Dormir avec un bonnet ou une taie en satin pour réduire les frottements.",
          "Masser le cuir chevelu quelques minutes lors de l'application des soins.",
        ],
      },
      {
        h: "Une routine simple sur la semaine",
        p: "Un lavage doux une fois par semaine, suivi d'un après-shampoing ou d'un masque, puis une crème sans rinçage pour nourrir les longueurs. Entre deux lavages, réhydratez au besoin. La régularité compte plus que le nombre de produits : une routine tenue plusieurs mois donne une idée plus juste des résultats.",
      },
      {
        h: "Quelles attentes avoir ?",
        p: "Aucun soin cosmétique ne peut garantir un résultat ni modifier la vitesse naturelle de pousse fixée par votre organisme. Un bon soin aide à garder un cuir chevelu sain et des longueurs moins cassantes. Si vous constatez une perte de cheveux inhabituelle, consultez notre guide sur la chute de cheveux.",
      },
    ],
    faq: [
      { q: "Les cheveux afro poussent-ils moins vite ?", a: "Pas forcément. La vitesse varie surtout d'une personne à l'autre. La forme bouclée et la casse donnent souvent l'impression d'une pousse plus lente." },
      { q: "Combien de temps avant de voir une différence ?", a: "Comptez plusieurs mois de routine régulière pour juger la rétention de longueur. Les résultats varient selon chaque personne." },
    ],
    netLinks: [
      { label: "Guide complet : faire pousser les cheveux crépus", href: `${NET}/conseils/comment-faire-pousser-cheveux-crepus` },
      { label: "Soins pousse des cheveux afro", href: `${NET}/pousse-des-cheveux` },
      { label: "Sérum Extrême Repouss", href: `${NET}/produit/extreme-repouss` },
      { label: "Faire son diagnostic cheveux", href: `${NET}/diagnostic-cheveux-afro` },
    ],
  },
  {
    slug: "routine-anti-casse-cheveux-afro",
    breadcrumb: "Routine anti-casse",
    title: "Éviter la casse des cheveux afro : routine et bons gestes | Ozentya",
    description:
      "Casse des cheveux afro et crépus : causes fréquentes, routine anti-casse simple et gestes à éviter pour préserver vos longueurs au quotidien.",
    h1: "Cheveux afro : une routine simple pour limiter la casse",
    intro:
      "La casse est l'une des premières causes de perte de longueur sur cheveux afro et crépus. Contrairement à la chute (le cheveu tombe avec sa racine), la casse correspond à un cheveu qui se rompt le long de la tige. Bonne nouvelle : elle dépend en grande partie des soins et des gestes du quotidien.",
    sections: [
      {
        h: "Chute ou casse : comment faire la différence",
        p: "Observez les cheveux tombés : un cheveu qui a chuté est long et présente souvent un petit bulbe clair à une extrémité. Un cheveu cassé est plus court et sans bulbe. Si vous constatez surtout de la chute, ou des zones clairsemées, référez-vous à notre guide sur la chute de cheveux.",
      },
      {
        h: "Les causes de casse les plus courantes",
        list: [
          "Un manque d'hydratation qui rend la fibre rigide.",
          "Un démêlage à sec ou trop rapide.",
          "Des coiffures trop serrées ou portées trop longtemps.",
          "La chaleur (lisseur, sèche-cheveux très chaud) et certains traitements chimiques.",
          "Les frottements nocturnes sur des tissus en coton.",
        ],
      },
      {
        h: "La routine anti-casse en 5 étapes",
        list: [
          "Laver en douceur avec un shampoing adapté, sans frotter les longueurs.",
          "Appliquer un après-shampoing ou un masque et démêler à ce moment-là, avec les doigts ou un peigne à dents larges.",
          "Appliquer une crème sans rinçage sur cheveux humides.",
          "Sceller avec une huile légère sur les pointes.",
          "Protéger la nuit avec du satin et choisir des coiffures protectrices confortables.",
        ],
      },
      {
        h: "Les gestes à éviter",
        list: [
          "Tirer sur les nœuds ou démêler sur cheveux secs.",
          "Multiplier les coiffages serrés sur les tempes.",
          "Utiliser la chaleur sans protection thermique.",
          "Changer de routine trop souvent sans laisser le temps d'observer.",
        ],
      },
    ],
    faq: [
      { q: "Faut-il couper ses pointes ?", a: "Des pointes très abîmées ou fourchues continuent souvent de casser. Une coupe d'entretien légère et régulière peut aider à préserver la longueur saine." },
      { q: "Les huiles suffisent-elles à hydrater ?", a: "Non : l'hydratation vient surtout de l'eau et des soins aqueux. L'huile aide plutôt à sceller cette hydratation." },
    ],
    netLinks: [
      { label: "Guide : éviter la casse des cheveux afro", href: `${NET}/conseils/eviter-casse-cheveux-afro` },
      { label: "Chute ou casse : faire la différence", href: `${NET}/conseils/difference-chute-casse-cheveux` },
      { label: "Masque Suprême", href: `${NET}/produit/masque-supreme` },
      { label: "Crème Reconstructrice Cactus", href: `${NET}/produit/creme-reconstructrice-cactus` },
    ],
  },
  {
    slug: "chute-cheveux-afro-quand-consulter",
    breadcrumb: "Chute de cheveux : quand consulter",
    title: "Chute de cheveux afro : causes possibles et quand consulter | Ozentya",
    description:
      "Chute de cheveux sur cheveux afro : causes fréquentes, alopécie de traction et signes qui doivent amener à consulter un médecin ou un dermatologue.",
    h1: "Chute de cheveux afro : causes possibles et quand consulter",
    intro:
      "Perdre chaque jour quelques dizaines de cheveux est normal : c'est le renouvellement naturel. Une chute qui augmente nettement, des zones clairsemées ou des plaques sans cheveux doivent en revanche être prises au sérieux. Ce guide donne des repères généraux : seul un médecin ou un dermatologue peut poser un diagnostic.",
    sections: [
      {
        h: "Des causes fréquentes et variées",
        list: [
          "Les tractions répétées (tresses, tissages, extensions ou queues de cheval serrées), surtout sur les tempes et la ligne frontale.",
          "Les périodes de stress, de fatigue, de régime ou de maladie, qui peuvent entraîner une chute diffuse quelques semaines plus tard.",
          "Les variations hormonales (après une grossesse, à la ménopause…).",
          "Certaines carences, des problèmes de thyroïde ou des affections du cuir chevelu.",
          "Des facteurs héréditaires.",
        ],
      },
      {
        h: "L'alopécie de traction, un point d'attention",
        p: "Sur cheveux afro, les coiffures serrées portées longtemps peuvent fragiliser les racines, notamment aux tempes. Au début, desserrer les coiffures et laisser des pauses entre deux coiffages protecteurs peut aider. Une prise en charge précoce par un professionnel de santé est importante, car une traction prolongée peut entraîner une perte durable.",
      },
      {
        h: "Les signes qui doivent amener à consulter",
        list: [
          "Une chute brutale ou très abondante.",
          "Des plaques rondes sans cheveux ou des zones qui s'élargissent.",
          "Des démangeaisons, rougeurs, douleurs, croûtes ou squames sur le cuir chevelu.",
          "Une chute accompagnée d'autres symptômes (fatigue importante, perte de poids…).",
          "Une chute qui dure depuis plus de quelques mois sans amélioration.",
        ],
      },
      {
        h: "Et les soins cosmétiques ?",
        p: "Les soins capillaires sont des cosmétiques, pas des médicaments : ils ne traitent pas une maladie et ne garantissent pas la repousse. Ils peuvent accompagner une routine d'entretien du cuir chevelu et des longueurs, en complément — jamais à la place — de l'avis d'un professionnel de santé.",
      },
    ],
    faq: [
      { q: "Combien de cheveux perd-on par jour normalement ?", a: "On cite souvent quelques dizaines par jour, avec des variations selon les saisons et les personnes. C'est surtout un changement net par rapport à d'habitude qui doit alerter." },
      { q: "Qui consulter en cas de chute ?", a: "Votre médecin traitant en premier lieu, qui pourra vous orienter vers un dermatologue si nécessaire." },
    ],
    netLinks: [
      { label: "Chute de cheveux afro et alopécie : précautions", href: `${NET}/chute-de-cheveux-alopecie` },
      { label: "Reconnaître les signes d'une alopécie", href: `${NET}/conseils/reconnaitre-alopecie-signes` },
      { label: "Alopécie de traction et cheveux afro", href: `${NET}/conseils/alopecie-de-traction-cheveux-afro` },
      { label: "Soins du cuir chevelu", href: `${NET}/cuir-chevelu` },
    ],
  },
  {
    slug: "tempes-degarnies-cheveux-afro",
    breadcrumb: "Tempes dégarnies",
    title: "Tempes dégarnies cheveux afro : causes et bons gestes | Ozentya",
    description:
      "Tempes et lisières dégarnies sur cheveux afro : rôle des coiffures serrées, gestes pour les préserver et signes qui doivent amener à consulter.",
    h1: "Tempes dégarnies sur cheveux afro : comprendre et protéger ses lisières",
    intro:
      "Les tempes et la ligne frontale (les « edges ») sont les zones les plus fines et les plus fragiles de la chevelure. Sur cheveux afro, elles sont souvent les premières à se dégarnir, notamment à cause des tractions répétées. Repérer tôt les signes et adapter ses habitudes permet de mieux les préserver. Ce guide est informatif et ne remplace pas l'avis d'un professionnel de santé.",
    sections: [
      {
        h: "Pourquoi les tempes se dégarnissent",
        list: [
          "Les coiffures serrées (tresses, nattes collées, tissages, queues de cheval, extensions) qui tirent sur les racines.",
          "Le poids des rajouts et le port prolongé sans pause.",
          "Les frottements : bandeaux, foulards serrés, perruques mal ajustées.",
          "Les gels et brossages appliqués trop souvent pour « plaquer » les lisières.",
          "D'autres causes possibles : hormonales, héréditaires ou liées à une affection du cuir chevelu.",
        ],
      },
      {
        h: "Les signes à surveiller",
        list: [
          "Des petits boutons ou une sensibilité au niveau des racines après une pose.",
          "Une lisière qui recule ou des cheveux de plus en plus fins aux tempes.",
          "Une sensation de tiraillement ou de douleur sous une coiffure.",
        ],
      },
      {
        h: "Les bons gestes pour protéger ses lisières",
        list: [
          "Demander à desserrer les tempes lors des poses et refuser une coiffure qui fait mal.",
          "Alterner coiffures protectrices et périodes de repos, sans tension sur les tempes.",
          "Limiter le poids et la durée de port des rajouts.",
          "Hydrater les lisières avec douceur et masser légèrement le cuir chevelu.",
          "Utiliser le gel ou l'edge control avec parcimonie, avec une brosse souple, et bien nettoyer ensuite.",
          "Protéger les cheveux la nuit avec un bonnet ou une taie en satin non serré.",
        ],
      },
      {
        h: "Quand consulter ?",
        p: "Si les tempes continuent de se dégarnir malgré des coiffures moins serrées, si la peau devient lisse et brillante, ou si vous ressentez douleurs, démangeaisons ou rougeurs, consultez votre médecin ou un dermatologue. Plus l'alopécie de traction est prise en charge tôt, meilleures sont les chances de préserver les follicules.",
      },
    ],
    faq: [
      { q: "Les tempes peuvent-elles se regarnir ?", a: "Cela dépend de la cause et de l'ancienneté du problème. Supprimer la traction rapidement est le premier geste ; seul un professionnel de santé peut évaluer la situation." },
      { q: "Faut-il arrêter les coiffures protectrices ?", a: "Pas forcément : l'essentiel est qu'elles ne soient pas serrées, ni trop lourdes, ni portées trop longtemps." },
    ],
    netLinks: [
      { label: "Alopécie de traction et cheveux afro", href: `${NET}/conseils/alopecie-de-traction-cheveux-afro` },
      { label: "Zones clairsemées : que faire ?", href: `${NET}/conseils/zones-clairsemees-cheveux-afro` },
      { label: "Sérum Extrême Repouss", href: `${NET}/produit/extreme-repouss` },
      { label: "Edge Control Ozentya", href: `${NET}/produit/edge-control` },
    ],
  },
  {
    slug: "cheveux-4c",
    breadcrumb: "Cheveux 4C",
    title: "Cheveux 4C : comprendre et entretenir cette texture | Ozentya",
    description:
      "Cheveux 4C : caractéristiques, rétractation, hydratation, démêlage et routine simple pour prendre soin de cette texture très frisée au quotidien.",
    h1: "Cheveux 4C : bien comprendre et entretenir cette texture",
    intro:
      "Dans la classification courante des textures, le type 4C désigne des cheveux très serrés, en forme de petits zigzags, avec une boucle peu ou pas visible. C'est une texture magnifique et volumineuse, mais fragile : elle se dessèche vite, se rétracte beaucoup et casse facilement si elle est manipulée brusquement.",
    sections: [
      {
        h: "Les particularités des cheveux 4C",
        list: [
          "Une forte rétractation : les cheveux peuvent paraître jusqu'à plusieurs fois plus courts que leur longueur réelle.",
          "Une tendance à la sécheresse, car le sébum descend difficilement le long de la fibre très frisée.",
          "Des nœuds fréquents, surtout aux pointes.",
          "Une fibre souvent fine, donc sensible à la casse.",
        ],
      },
      {
        h: "Hydrater et sceller",
        p: "L'eau est la base de l'hydratation. Appliquez vos soins sur cheveux humides, puis scellez avec une crème et une huile (méthodes LOC ou LCO). Travaillez par sections pour que chaque mèche reçoive le soin. Entre deux lavages, réhydratez au besoin avec un spray d'eau ou un soin léger.",
      },
      {
        h: "Démêler sans casser",
        list: [
          "Démêler uniquement sur cheveux humides, enrichis d'après-shampoing ou de masque.",
          "Travailler en petites sections, d'abord avec les doigts, puis au peigne à dents larges.",
          "Commencer par les pointes et remonter progressivement vers les racines.",
          "Garder les cheveux en vanilles ou en tresses lâches entre deux lavages pour limiter les nœuds.",
        ],
      },
      {
        h: "Une routine simple pour cheveux 4C",
        list: [
          "1 fois par semaine : shampoing doux, puis masque ou après-shampoing et démêlage.",
          "Sur cheveux essorés : crème sans rinçage, puis une huile légère sur les pointes.",
          "Le soir : massage du cuir chevelu et protection en satin.",
          "Coiffures protectrices non serrées, avec des pauses entre deux poses.",
        ],
      },
    ],
    faq: [
      { q: "Les cheveux 4C poussent-ils moins ?", a: "Pas nécessairement. La rétractation et la casse donnent souvent cette impression : la rétention de longueur est le vrai enjeu." },
      { q: "Faut-il étirer ses cheveux 4C ?", a: "Ce n'est pas obligatoire. Des méthodes sans chaleur (tresses, vanilles, bantu knots) permettent d'étirer et de limiter les nœuds si vous le souhaitez." },
    ],
    netLinks: [
      { label: "Guide d'entretien des cheveux 4C", href: `${NET}/conseils/cheveux-4c-entretien-guide` },
      { label: "Méthode LOC / LCO", href: `${NET}/conseils/methode-loc-lco-cheveux-afro` },
      { label: "Hydratation des cheveux afro", href: `${NET}/hydratation-cheveux-afro` },
      { label: "Masque Suprême", href: `${NET}/produit/masque-supreme` },
    ],
  },
];

export const getHairGuide = (slug) => HAIR_GUIDES.find((g) => g.slug === slug);
